<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import { isset, sanitizeObject } from "@/helpers/object";

const { t } = useI18n();

interface TextareaInputProps {
    label?: string;
    modelValue: string;
    description?: string;
    placeholder?: string;
    error?: string;
    showErrorText?: boolean;
    size?: "sm" | "md" | "lg";
    rows?: number;
    disabled?: boolean;
    required?: boolean;
    autoResize?: boolean;
    noBorder?: boolean;
    readOnly?: boolean;
    fontSize?: string;
    noPadding?: boolean;
}

const props = withDefaults(defineProps<TextareaInputProps>(), {
    size: "md",
    rows: 2,
    showErrorText: true,
    noPadding: false,
});

// Synch with input focused state
const isFocused = ref(false);

// Handle parent v-model value update
const emit = defineEmits(["update:modelValue"]);
// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const handleChange = (event: any) => {
    const inputValue = event.target.value;
    emit("update:modelValue", inputValue);
};

const handleBlur = () => {
    isFocused.value = false;
};

let readOnlyTextContainer = ref<HTMLSpanElement | null>(null);
let canDisplayExpandButton = ref<boolean>(false);
onMounted(() => {
    if (props.readOnly && readOnlyTextContainer.value) {
        canDisplayExpandButton.value = readOnlyTextContainer.value?.scrollHeight > readOnlyTextContainer.value?.clientHeight;
    }
});

const allLinesDisplayed = ref<boolean>(false);
const displayMoreLines = () => {
    allLinesDisplayed.value = !allLinesDisplayed.value;
};

const textareaReadOnlyClasses = computed(() => {
    return {
        "ds-text-area-input-component__read-only-container": true,
        "ds-text-area-input-component__read-only-container--error": !!props.error,
        "ds-text-area-input-component__read-only-container--expanded": allLinesDisplayed.value,
    };
});

const readOnlyTextContainerStyle = computed(() => {
    return `-webkit-line-clamp: ${props.rows}; line-clamp: ${props.rows};`;
});

const expandButtonLabel = computed(() => {
    return allLinesDisplayed.value ? t("global.actions.reduce") : t("global.actions.viewAll");
});
</script>
<template>
    <div class="ds-text-area-input-component" :class="[size, { disabled, noPadding }]">
        <label v-if="label" :for="label">
            {{ label }}
            <span v-if="required" class="required-asterisk">*</span>
        </label>
        <div class="description" v-if="description">{{ description }}</div>
        <div class="textarea-container">
            <div v-if="readOnly" :class="textareaReadOnlyClasses">
                <span
                    ref="readOnlyTextContainer"
                    class="ds-text-area-input-component__read-only-text"
                    :style="readOnlyTextContainerStyle"
                >
                    {{ props.modelValue }}
                </span>
                <div v-if="canDisplayExpandButton" class="expand-btn" @click="displayMoreLines">
                    {{ expandButtonLabel }}
                </div>
            </div>

            <textarea
                v-else
                id="textarea"
                :name="label"
                :placeholder="placeholder"
                :disabled="disabled"
                :value="modelValue"
                @input="handleChange"
                @blur="handleBlur"
                @focus="isFocused = true"
                :rows="props.rows"
                :class="[{ error: isset(props.error), focus: isFocused, disabled, autoResize, noBorder }]"
                :required="required"
                :style="sanitizeObject({ fontSize })"
            ></textarea>
        </div>
        <div v-if="props.error && props.showErrorText" class="input-error">{{ props.error }}</div>
    </div>
</template>
<style scoped lang="scss">
@mixin inputShadow($color) {
    box-shadow: 0 0 0 3px $color;
}

.ds-text-area-input-component {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    position: relative;
    width: 100%;

    .textarea-container {
        position: relative;
        width: 100%;
    }

    label {
        font-size: 14px;
        font-weight: 500;
        line-height: 20px;
    }

    .description {
        color: $text-sub-600;
        font-size: 13px;
    }

    &.lg {
        label {
            font-size: 16px;
            line-height: 24px;
        }
        .description {
            font-size: 14px;
        }
    }

    &.disabled label {
        color: $text-disabled-300;
    }

    &.noPadding {
        padding: 0;
    }

    .required-asterisk {
        color: $blue-base;
    }

    .ti {
        font-size: 18px;
    }

    textarea {
        height: max-content;
        border-radius: 8px;
        border: 1px solid $stroke-sub-300;
        resize: vertical;
        transition: 0.2s;

        padding: 10px 12px;
        color: $text-main-900;
        font-weight: 400;
        font-size: 13px;
        width: 100%;
        field-sizing: fixed;

        &:disabled {
            border-color: $bg-weak-100;
            background-color: $bg-weak-100;
            color: $text-disabled-300;
            resize: none;
        }

        &:hover:not(.disabled, .focus, .error) {
            border-color: $stroke-strong-900;
        }

        &.focus {
            border-color: $blue-base;
        }

        &.error {
            border-color: $red-base;
        }

        &.autoResize {
            field-sizing: content;
        }

        &.noBorder {
            border: none;
            padding: 5px 0;
        }
        &::placeholder {
            color: $text-soft-400;
        }
    }

    div.input-error {
        position: absolute;
        color: $red-base;
        font-size: 13px;
        bottom: -18px;
        left: 0;
        padding: 0;
    }

    &__read-only {
        &-container {
            position: relative;
            padding: 5px 8px;
            border-radius: 6px;

            &--error {
                border: 1px solid $red-base;
            }

            &--expanded {
                .ds-text-area-input-component__read-only-text {
                    -webkit-line-clamp: none !important;
                    line-clamp: none !important;
                }
            }

            &:hover {
                background-color: $bg-weak-100;

                .expand-btn {
                    opacity: 1;
                }
            }

            .expand-btn {
                opacity: 0;
                position: absolute;
                top: 5px;
                right: 6px;
                z-index: 1;
                padding: 0 4px;
                color: $text-sub-600;
                @include font-size-10;
                font-weight: $medium;
                border-radius: 4px;
                background-color: $bg-white-0;
                box-shadow: 0 0 6px 4px $neutral-100;
                cursor: pointer;
            }
        }

        &-text {
            overflow: hidden;
            color: $text-main-900;
            text-overflow: ellipsis;
            @include font-size-13;
            font-weight: $medium;
            white-space: pre-wrap;
            display: -webkit-box;
            -webkit-box-orient: vertical;
        }
    }
}
</style>
